import { useState, useEffect } from 'react';
import {
  Download,
  Search,
  TrendingUp,
  TrendingDown,
  IndianRupee,
  CreditCard,
  Percent,
  Receipt,
  Wallet,
  Send,
  History
} from 'lucide-react';
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import apiService from '../services/api';
import toast from 'react-hot-toast';
import { getTodayIST, formatDateTime } from '../utils/dateUtils';
import {
  formatPeriodLabel,
  formatRate,
  describeFee,
  buildAnalyticsCsv,
  downloadCsv
} from '../utils/analytics';

const MAX_DAYS = { daily: 93, weekly: 366, monthly: 366 };

// Shift a YYYY-MM-DD date string by a number of days
const shiftDate = (dateStr, days) => {
  const date = new Date(`${dateStr}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
};

const daysBetween = (from, to) =>
  Math.round((new Date(`${to}T00:00:00Z`) - new Date(`${from}T00:00:00Z`)) / 86400000) + 1;

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 2
  }).format(amount || 0);
};

const formatCompact = (amount) => {
  if (amount >= 10000000) return `₹${(amount / 10000000).toFixed(1)}Cr`;
  if (amount >= 100000) return `₹${(amount / 100000).toFixed(1)}L`;
  if (amount >= 1000) return `₹${(amount / 1000).toFixed(0)}K`;
  return `₹${amount}`;
};

const formatNumber = (value) => new Intl.NumberFormat('en-IN').format(value || 0);

const formatHour = (hour) => {
  const suffix = hour < 12 ? 'AM' : 'PM';
  return `${hour % 12 === 0 ? 12 : hour % 12} ${suffix}`;
};

// Change vs previous period; `points` shows an absolute difference (for percentages)
const Delta = ({ current, previous, points = false }) => {
  if (!previous && !points) return <span className="text-gray-400">No data in previous period</span>;
  const diff = points ? current - previous : ((current - previous) / previous) * 100;
  if (!isFinite(diff)) return null;
  const up = diff >= 0;
  const Icon = up ? TrendingUp : TrendingDown;
  return (
    <span className={`inline-flex items-center ${up ? 'text-green-600' : 'text-red-600'}`}>
      <Icon size={14} className="mr-1" />
      {up ? '+' : ''}{diff.toFixed(1)}{points ? ' pts' : '%'}
      <span className="text-gray-400 ml-1">vs previous period</span>
    </span>
  );
};

// Full class names so Tailwind keeps them in the build
const COLOR_CLASSES = {
  green: ['bg-green-100', 'text-green-600'],
  blue: ['bg-blue-100', 'text-blue-600'],
  purple: ['bg-purple-100', 'text-purple-600'],
  orange: ['bg-orange-100', 'text-orange-600'],
  indigo: ['bg-indigo-100', 'text-indigo-600'],
  teal: ['bg-teal-100', 'text-teal-600'],
  pink: ['bg-pink-100', 'text-pink-600'],
  red: ['bg-red-100', 'text-red-600']
};

const StatCard = ({ label, value, icon: Icon, color, footer }) => (
  <div className="card p-6">
    <div className="flex items-center justify-between">
      <div>
        <p className="text-sm font-medium text-gray-600">{label}</p>
        <p className="text-2xl font-bold text-gray-900">{value}</p>
      </div>
      <div className={`p-3 ${COLOR_CLASSES[color][0]} rounded-full`}>
        <Icon className={`h-6 w-6 ${COLOR_CLASSES[color][1]}`} />
      </div>
    </div>
    {footer && <div className="mt-4 text-sm">{footer}</div>}
  </div>
);

const Analytics = () => {
  const today = getTodayIST();
  const [filters, setFilters] = useState({
    mode: 'daily',
    dateFrom: shiftDate(today, -29),
    dateTo: today
  });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showEmpty, setShowEmpty] = useState(false);

  useEffect(() => {
    loadAnalytics(filters);
  }, []);

  const loadAnalytics = async (params) => {
    if (params.dateFrom > params.dateTo) {
      toast.error('From date must be before To date');
      return;
    }
    if (daysBetween(params.dateFrom, params.dateTo) > MAX_DAYS[params.mode]) {
      toast.error(`Maximum range for ${params.mode} mode is ${MAX_DAYS[params.mode]} days`);
      return;
    }

    try {
      setLoading(true);
      const response = await apiService.getAnalytics({
        mode: params.mode,
        from_date: params.dateFrom,
        to_date: params.dateTo
      });
      if (response.success) {
        setData(response.data);
      }
    } catch (error) {
      console.error('Failed to load analytics:', error);
      toast.error(error.response?.data?.error?.message || 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const applyPreset = (preset) => {
    let next;
    if (preset === 'today') next = { ...filters, mode: 'daily', dateFrom: today, dateTo: today };
    if (preset === '7d') next = { ...filters, mode: 'daily', dateFrom: shiftDate(today, -6), dateTo: today };
    if (preset === '30d') next = { ...filters, mode: 'daily', dateFrom: shiftDate(today, -29), dateTo: today };
    if (preset === 'month') next = { ...filters, mode: 'daily', dateFrom: `${today.slice(0, 7)}-01`, dateTo: today };
    if (preset === '6m') next = { ...filters, mode: 'monthly', dateFrom: `${shiftDate(`${today.slice(0, 7)}-01`, -150).slice(0, 7)}-01`, dateTo: today };
    setFilters(next);
    loadAnalytics(next);
  };

  const exportCsv = () => {
    if (!data) return;
    downloadCsv(
      buildAnalyticsCsv(data, showEmpty),
      `analytics_${data.mode}_${data.from_date}_to_${data.to_date}.csv`
    );
    toast.success('Analytics exported');
  };

  const totals = data?.totals;
  const previous = data?.previous_period?.payin;
  const tableRows = data
    ? data.rows.filter(r => showEmpty || r.payin.total > 0 || r.payout.total > 0)
    : [];
  const chartData = data
    ? data.rows.map(r => ({
        label: formatPeriodLabel(r.period, data.mode).replace('Week of ', ''),
        amount: r.payin.amount,
        commission: r.payin.fee
      }))
    : [];
  const hasPayouts = totals && totals.payout.total > 0;
  const maxTicketAmount = data ? Math.max(...data.ticket_sizes.map(t => t.amount), 1) : 1;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Analytics</h1>
        <p className="mt-1 text-sm text-gray-600">
          Payin volume, commission and payouts by day, week or month (IST)
        </p>
      </div>

      {/* Filters */}
      <div className="card p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 items-end">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Mode</label>
            <select
              value={filters.mode}
              onChange={(e) => handleFilterChange('mode', e.target.value)}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
            >
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">From</label>
            <input
              type="date"
              value={filters.dateFrom}
              max={today}
              onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">To</label>
            <input
              type="date"
              value={filters.dateTo}
              max={today}
              onChange={(e) => handleFilterChange('dateTo', e.target.value)}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
            />
          </div>
          <button
            onClick={() => loadAnalytics(filters)}
            disabled={loading}
            className="btn-primary inline-flex items-center justify-center"
          >
            <Search size={16} className="mr-2" />
            Search
          </button>
          <button
            onClick={exportCsv}
            disabled={loading || !data}
            className="btn-secondary inline-flex items-center justify-center"
          >
            <Download size={16} className="mr-2" />
            Export CSV
          </button>
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          {[
            ['today', 'Today'],
            ['7d', 'Last 7 days'],
            ['30d', 'Last 30 days'],
            ['month', 'This month'],
            ['6m', 'Last 6 months']
          ].map(([key, label]) => (
            <button
              key={key}
              onClick={() => applyPreset(key)}
              disabled={loading}
              className="px-3 py-1 text-xs font-medium rounded-full border border-gray-300 text-gray-700 hover:bg-gray-50"
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {loading && !data ? (
        <div className="flex items-center justify-center min-h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        </div>
      ) : data && (
        <div className={`space-y-6 ${loading ? 'opacity-50 pointer-events-none' : ''}`}>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <StatCard
              label="Payin Amount"
              value={formatCurrency(totals.payin.amount)}
              icon={IndianRupee}
              color="green"
              footer={<Delta current={totals.payin.amount} previous={previous.amount} />}
            />
            <StatCard
              label="Successful Transactions"
              value={`${formatNumber(totals.payin.success)} / ${formatNumber(totals.payin.total)}`}
              icon={CreditCard}
              color="blue"
              footer={<Delta current={totals.payin.success} previous={previous.success} />}
            />
            <StatCard
              label="Success Rate"
              value={`${totals.payin.success_rate.toFixed(1)}%`}
              icon={Percent}
              color="purple"
              footer={previous.total > 0
                ? <Delta current={totals.payin.success_rate} previous={previous.success_rate} points />
                : <span className="text-gray-400">No data in previous period</span>}
            />
            <StatCard
              label="Commission"
              value={formatCurrency(totals.payin.fee)}
              icon={Receipt}
              color="orange"
              footer={
                <span className="text-gray-600">
                  Effective rate: {totals.payin.effective_rate != null ? `${totals.payin.effective_rate.toFixed(2)}%` : '—'}
                </span>
              }
            />
            <StatCard
              label="Net Payin"
              value={formatCurrency(totals.payin.net_amount)}
              icon={Wallet}
              color="indigo"
              footer={<span className="text-gray-600">Avg ticket: {formatCurrency(totals.payin.avg_ticket)}</span>}
            />
            <StatCard
              label="Payout Amount"
              value={formatCurrency(totals.payout.amount)}
              icon={Send}
              color="teal"
              footer={
                <span className="text-gray-600">
                  {formatNumber(totals.payout.success)} successful / {formatNumber(totals.payout.total)} total
                </span>
              }
            />
            <StatCard
              label="Payout Fees"
              value={formatCurrency(totals.payout.fee)}
              icon={Receipt}
              color="pink"
              footer={<span className="text-gray-600">Current: {describeFee(data.current_fees.payout)}</span>}
            />
            <StatCard
              label="Total Fees"
              value={formatCurrency(totals.total_fees)}
              icon={Receipt}
              color="red"
              footer={<span className="text-gray-600">Payin commission + payout fees</span>}
            />
          </div>

          {/* Payin Failure Breakdown */}
          <div className="card p-4">
            <div className="flex flex-wrap gap-x-8 gap-y-2 text-sm">
              <span className="text-green-600">Success: {formatNumber(totals.payin.success)}</span>
              <span className="text-red-600">Failed: {formatNumber(totals.payin.failed)}</span>
              <span className="text-gray-600">Expired: {formatNumber(totals.payin.expired)}</span>
              <span className="text-yellow-600">Pending: {formatNumber(totals.payin.pending)}</span>
              <span className="text-gray-500">
                Previous period ({data.previous_period.from_date} to {data.previous_period.to_date}):{' '}
                {formatCurrency(previous.amount)} from {formatNumber(previous.success)} successful
              </span>
            </div>
          </div>

          {/* Trend Chart */}
          <div className="card p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Payin Amount &amp; Commission</h3>
            <ResponsiveContainer width="100%" height={300}>
              <ComposedChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 12 }} minTickGap={16} />
                <YAxis yAxisId="amount" tickFormatter={formatCompact} tick={{ fontSize: 12 }} />
                <YAxis yAxisId="commission" orientation="right" tickFormatter={formatCompact} tick={{ fontSize: 12 }} />
                <Tooltip formatter={(value, name) => [formatCurrency(value), name]} />
                <Legend />
                <Bar yAxisId="amount" dataKey="amount" name="Payin Amount" fill="#3B82F6" radius={[4, 4, 0, 0]} />
                <Line yAxisId="commission" type="monotone" dataKey="commission" name="Commission" stroke="#F97316" strokeWidth={2} dot={false} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>

          {/* Period Table */}
          <div className="card">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">
                {data.mode === 'daily' ? 'Daily' : data.mode === 'weekly' ? 'Weekly' : 'Monthly'} Breakdown
              </h3>
              <label className="inline-flex items-center text-sm text-gray-600">
                <input
                  type="checkbox"
                  checked={showEmpty}
                  onChange={(e) => setShowEmpty(e.target.checked)}
                  className="mr-2 rounded border-gray-300"
                />
                Show periods with no transactions
              </label>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <th className="px-4 py-3">#</th>
                    <th className="px-4 py-3">{data.mode === 'monthly' ? 'Month' : data.mode === 'weekly' ? 'Week' : 'Date'}</th>
                    <th className="px-4 py-3 text-right">Amount</th>
                    <th className="px-4 py-3 text-right">Txns (ok / total)</th>
                    <th className="px-4 py-3 text-right">Failed</th>
                    <th className="px-4 py-3 text-right">Expired</th>
                    <th className="px-4 py-3 text-right">Success %</th>
                    <th className="px-4 py-3 text-right">Rate</th>
                    <th className="px-4 py-3 text-right">Commission</th>
                    <th className="px-4 py-3 text-right">Net Amount</th>
                    {hasPayouts && <th className="px-4 py-3 text-right">Payouts</th>}
                    {hasPayouts && <th className="px-4 py-3 text-right">Payout Fee</th>}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {tableRows.length === 0 ? (
                    <tr>
                      <td colSpan={hasPayouts ? 12 : 10} className="px-4 py-8 text-center text-gray-500">
                        No transactions in this period
                      </td>
                    </tr>
                  ) : tableRows.map((row, index) => (
                    <tr key={row.period} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-gray-500">{index + 1}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-gray-900">{formatPeriodLabel(row.period, data.mode)}</td>
                      <td className="px-4 py-3 text-right font-medium text-gray-900">{formatCurrency(row.payin.amount)}</td>
                      <td className="px-4 py-3 text-right">{formatNumber(row.payin.success)} / {formatNumber(row.payin.total)}</td>
                      <td className="px-4 py-3 text-right text-red-600">{formatNumber(row.payin.failed)}</td>
                      <td className="px-4 py-3 text-right text-gray-500">{formatNumber(row.payin.expired)}</td>
                      <td className="px-4 py-3 text-right">{row.payin.total > 0 ? `${row.payin.success_rate.toFixed(1)}%` : '—'}</td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">{formatRate(row.payin)}</td>
                      <td className="px-4 py-3 text-right text-orange-600">{formatCurrency(row.payin.fee)}</td>
                      <td className="px-4 py-3 text-right">{formatCurrency(row.payin.net_amount)}</td>
                      {hasPayouts && (
                        <td className="px-4 py-3 text-right whitespace-nowrap">
                          {formatCurrency(row.payout.amount)}
                          <span className="text-gray-400 ml-1">({row.payout.success})</span>
                        </td>
                      )}
                      {hasPayouts && <td className="px-4 py-3 text-right">{formatCurrency(row.payout.fee)}</td>}
                    </tr>
                  ))}
                </tbody>
                {tableRows.length > 0 && (
                  <tfoot className="bg-gray-50 font-semibold text-gray-900">
                    <tr>
                      <td className="px-4 py-3"></td>
                      <td className="px-4 py-3">Total</td>
                      <td className="px-4 py-3 text-right">{formatCurrency(totals.payin.amount)}</td>
                      <td className="px-4 py-3 text-right">{formatNumber(totals.payin.success)} / {formatNumber(totals.payin.total)}</td>
                      <td className="px-4 py-3 text-right">{formatNumber(totals.payin.failed)}</td>
                      <td className="px-4 py-3 text-right">{formatNumber(totals.payin.expired)}</td>
                      <td className="px-4 py-3 text-right">{totals.payin.success_rate.toFixed(1)}%</td>
                      <td className="px-4 py-3 text-right">
                        {totals.payin.effective_rate != null ? `${totals.payin.effective_rate.toFixed(2)}%` : '—'}
                      </td>
                      <td className="px-4 py-3 text-right text-orange-600">{formatCurrency(totals.payin.fee)}</td>
                      <td className="px-4 py-3 text-right">{formatCurrency(totals.payin.net_amount)}</td>
                      {hasPayouts && <td className="px-4 py-3 text-right">{formatCurrency(totals.payout.amount)}</td>}
                      {hasPayouts && <td className="px-4 py-3 text-right">{formatCurrency(totals.payout.fee)}</td>}
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
            <p className="px-6 py-3 text-xs text-gray-500 border-t border-gray-200">
              Commission is the fee charged on each successful payin at the rate active when it was created.
              A rate range means the rate changed during that period.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Hour of Day */}
            <div className="card p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-1">Hour of Day (IST)</h3>
              <p className="text-sm text-gray-500 mb-4">Payin attempts and success rate by hour</p>
              <ResponsiveContainer width="100%" height={280}>
                <ComposedChart data={data.hourly.map(h => ({ ...h, label: formatHour(h.hour) }))}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} interval={2} />
                  <YAxis yAxisId="count" tick={{ fontSize: 12 }} />
                  <YAxis yAxisId="rate" orientation="right" domain={[0, 100]} tickFormatter={(v) => `${v}%`} tick={{ fontSize: 12 }} />
                  <Tooltip
                    formatter={(value, name) => [name === 'Success Rate' ? `${value}%` : formatNumber(value), name]}
                  />
                  <Legend />
                  <Bar yAxisId="count" dataKey="total" name="Attempts" fill="#93C5FD" radius={[3, 3, 0, 0]} />
                  <Bar yAxisId="count" dataKey="success" name="Successful" fill="#3B82F6" radius={[3, 3, 0, 0]} />
                  <Line yAxisId="rate" type="monotone" dataKey="success_rate" name="Success Rate" stroke="#10B981" strokeWidth={2} dot={false} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>

            {/* Ticket Size */}
            <div className="card p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-1">Ticket Size</h3>
              <p className="text-sm text-gray-500 mb-4">Successful amount and success rate by payin amount</p>
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <th className="py-2">Range</th>
                    <th className="py-2 text-right">Txns (ok / total)</th>
                    <th className="py-2 text-right">Success %</th>
                    <th className="py-2 text-right">Amount</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {data.ticket_sizes.map(t => (
                    <tr key={t.label}>
                      <td className="py-3 whitespace-nowrap text-gray-900">{t.label}</td>
                      <td className="py-3 text-right">{formatNumber(t.success)} / {formatNumber(t.total)}</td>
                      <td className="py-3 text-right">{t.total > 0 ? `${t.success_rate.toFixed(1)}%` : '—'}</td>
                      <td className="py-3 text-right">
                        <div className="font-medium text-gray-900">{formatCurrency(t.amount)}</div>
                        <div className="mt-1 h-1.5 bg-gray-100 rounded-full">
                          <div
                            className="h-1.5 bg-primary-500 rounded-full"
                            style={{ width: `${(t.amount / maxTicketAmount) * 100}%` }}
                          />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Fee Rates */}
          <div className="card p-6">
            <div className="flex items-center mb-4">
              <History className="h-5 w-5 text-gray-400 mr-2" />
              <h3 className="text-lg font-semibold text-gray-900">Fee Rates</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div className="rounded-lg bg-gray-50 p-4">
                <p className="text-sm text-gray-600">Current payin commission</p>
                <p className="text-xl font-bold text-gray-900">{describeFee(data.current_fees.payin)}</p>
              </div>
              <div className="rounded-lg bg-gray-50 p-4">
                <p className="text-sm text-gray-600">Current payout fee</p>
                <p className="text-base font-semibold text-gray-900">{describeFee(data.current_fees.payout)}</p>
              </div>
            </div>
            <h4 className="text-sm font-medium text-gray-900 mb-2">Rate changes</h4>
            {data.rate_changes.length === 0 ? (
              <p className="text-sm text-gray-500">No rate changes recorded</p>
            ) : (
              <ul className="divide-y divide-gray-100 text-sm">
                {data.rate_changes.map((change, index) => (
                  <li key={index} className="py-2 flex flex-wrap items-center justify-between gap-2">
                    <span>
                      <span className="font-medium capitalize">{change.fee_type}</span>
                      {': '}
                      {change.previous ? (
                        <>
                          <span className="text-gray-500 line-through mr-1">{describeFee(change.previous)}</span>
                          → {describeFee(change)}
                        </>
                      ) : (
                        <>set to {describeFee(change)}</>
                      )}
                    </span>
                    <span className="text-gray-500">{formatDateTime(change.effective_from)}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Analytics;
